import { useEffect, useState } from 'react';
import api from '../lib/api';

/**
 * Returns `{ has(featureKey), seats, planId, loading }` for the current
 * workspace. Reads `/api/billing/features` once on mount and caches.
 *
 * Used by Sidebar to hide nav items the plan doesn't include and by any
 * page that needs to show an "Upgrade to unlock" overlay instead of the
 * real content. The backend ALSO enforces every gated route at the
 * middleware layer, so this hook is purely UX — a clever client can't
 * bypass by mocking the response.
 */
let _cache = null;
let _inflight = null;

export function clearPlanFeaturesCache() { _cache = null; _inflight = null; }

export default function usePlanFeatures() {
  const [state, setState] = useState(() => _cache || { loading: true, plan_id: null, features: new Set(), seats_cap: 1 });

  useEffect(() => {
    if (_cache) { setState(_cache); return; }
    if (!_inflight) {
      _inflight = api.get('/billing/features').then(({ data }) => {
        _cache = {
          loading: false,
          plan_id: data.plan_id,
          features: new Set(data.features || []),
          seats_cap: data.seats_cap || 1,
          catalog: data.catalog || {},
        };
        return _cache;
      }).catch(() => {
        // Failsafe — assume full access so a flaky network doesn't blank
        // the UI. Backend still enforces the real gate.
        _cache = { loading: false, plan_id: null, features: new Set(), seats_cap: 1, catalog: {}, failed: true };
        return _cache;
      });
    }
    _inflight.then(c => setState(c));
  }, []);

  return {
    ...state,
    has: (key) => {
      if (state.failed) return true;
      if (!state.features || typeof state.features.has !== 'function') return false;
      return state.features.has(key);
    },
  };
}
